// components/ExpenseSummary.js
import React from 'react';
import PropTypes from 'prop-types';
import { styled, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import CategoryIcon from '@mui/icons-material/Category';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    Cell
} from 'recharts';

// Container with no padding or margins to align flush with parent
const SummaryContainer = styled(Box)(({ theme }) => ({
    width: '100%',
    overflowX: 'hidden',
    position: 'relative',
    zIndex: 0,
    padding: 0,
    margin: 0,
    boxSizing: 'border-box',
}));

// Adjusted grid container to remove default padding
const GridContainer = styled(Grid)(({ theme }) => ({
    width: '100%',
    margin: 0,
    padding: 0,
    '& .MuiGrid-item': {
        paddingTop: 0, // Remove top padding from all grid items
    },
    '& .MuiGrid-item:first-of-type': {
        paddingLeft: 0, // Remove left padding from leftmost items
    }
}));

// Enhanced card design with better visual styling
const SummaryCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2.5),
    borderRadius: theme.spacing(1.5),
    boxShadow: '0 3px 10px rgba(0,0,0,0.06)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 10px 20px rgba(0,0,0,0.08)',
    },
    background: theme.palette.background.paper,
}));

// Stats card with accent color
const StatCard = styled(SummaryCard)(({ theme, accentColor }) => ({
    borderTop: `4px solid ${accentColor || theme.palette.primary.main}`,
    position: 'relative',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,
        width: '80px',
        height: '80px',
        background: alpha(accentColor || theme.palette.primary.main, 0.04),
        borderRadius: '0 0 0 100%',
    }
}));

// Improved chart container
const ChartContainer = styled(Box)(({ theme }) => ({
    height: 300,
    marginTop: theme.spacing(2),
    width: '100%',
    overflow: 'hidden',
}));

// Enhanced title styling
const CardTitle = styled(Typography)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.text.primary,
    '& .MuiSvgIcon-root': {
        color: theme.palette.primary.main,
    }
}));

// Stats section with custom spacing
const StatsGrid = styled(GridContainer)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

// List item with improved styling
const ExpenseItem = styled(Box)(({ theme, index }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1.2, 1.5),
    borderRadius: theme.spacing(0.8),
    marginBottom: theme.spacing(0.5),
    backgroundColor: index % 2 === 0 ? alpha(theme.palette.primary.main, 0.03) : 'transparent',
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.07),
    },
    transition: 'background-color 0.2s ease',
}));

// Improved stat value styling
const StatValue = styled(Typography)(({ theme, large, color }) => ({
    fontWeight: 700,
    fontSize: large ? theme.typography.h4.fontSize : theme.typography.h6.fontSize,
    color: color || theme.palette.primary.main,
    marginBottom: theme.spacing(0.5),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}));

// Stat label with better visual hierarchy
const StatLabel = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption.fontSize,
    fontWeight: 600,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
}));

// Color palette
const COLORS = ['#3f51b5', '#f50057', '#00acc1', '#ff9800', '#4caf50', '#9c27b0', '#607d8b'];

// Enhanced tooltip design
const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <Box sx={{
                bgcolor: 'background.paper',
                p: 2,
                boxShadow: 3,
                borderRadius: 1.5,
                border: '1px solid #f0f0f0',
                minWidth: 180,
            }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>{label}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttachMoneyIcon fontSize="small" color="success" />
                    <Typography variant="body1" sx={{ fontWeight: 700, color: 'success.main' }}>
                        {payload[0].value.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'KSH',
                        })}
                    </Typography>
                </Box>
            </Box>
        );
    }
    return null;
};

const ExpenseSummary = ({ stats, isMobile }) => {
    // Prepare data for category breakdown
    const categoryData = Object.entries(stats.byCategory).map(([category, amount], index) => ({
        name: category,
        value: amount,
        color: COLORS[index % COLORS.length]
    })).sort((a, b) => b.value - a.value);

    // Prepare data for property breakdown
    const propertyData = Object.entries(stats.byProperty).map(([property, amount], index) => ({
        name: property,
        value: amount,
        color: COLORS[(index + 3) % COLORS.length]
    })).sort((a, b) => b.value - a.value);

    return (
        <SummaryContainer>
            {/* Stats Overview */}
            <StatsGrid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard accentColor="#3f51b5">
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
                            <Box sx={{ maxWidth: '75%' }}>
                                <StatLabel>TOTAL EXPENSES</StatLabel>
                                <StatValue large color="#3f51b5">{stats.totalExpenses}</StatValue>
                            </Box>
                            <ReceiptLongIcon fontSize="large" sx={{ color: '#3f51b5', opacity: 0.8 }} />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ position: 'relative', zIndex: 1 }}>
                            Expenses tracked across all properties
                        </Typography>
                    </StatCard>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <StatCard accentColor="#f50057">
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
                            <Box sx={{ maxWidth: '75%' }}>
                                <StatLabel>TOTAL SPENT</StatLabel>
                                <StatValue large color="#f50057">
                                    {stats.totalAmount.toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: 'KSH',
                                        maximumFractionDigits: 0,
                                    })}
                                </StatValue>
                            </Box>
                            <AttachMoneyIcon fontSize="large" sx={{ color: '#f50057', opacity: 0.8 }} />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ position: 'relative', zIndex: 1 }}>
                            Total amount spent on expenses
                        </Typography>
                    </StatCard>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <StatCard accentColor="#00acc1">
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
                            <Box sx={{ maxWidth: '75%' }}>
                                <StatLabel>TOP CATEGORY</StatLabel>
                                <StatValue color="#00acc1">
                                    {categoryData.length > 0 ? (
                                        categoryData[0].name
                                    ) : (
                                        'None'
                                    )}
                                </StatValue>
                            </Box>
                            <CategoryIcon fontSize="large" sx={{ color: '#00acc1', opacity: 0.8 }} />
                        </Box>
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ position: 'relative', zIndex: 1 }}>
                            {categoryData.length > 0 ? (
                                `${categoryData[0].value.toLocaleString('en-US', {
                                    style: 'currency',
                                    currency: 'KSH',
                                    maximumFractionDigits: 0,
                                })} (${Math.round(categoryData[0].value / stats.totalAmount * 100)}%)`
                            ) : (
                                'No expense data available'
                            )}
                        </Typography>
                    </StatCard>
                </Grid>
            </StatsGrid>

            {/* Charts and Breakdowns */}
            <GridContainer container spacing={3}>
                {/* Monthly Trend Chart */}
                <Grid item xs={12} md={8}>
                    <SummaryCard>
                        <CardTitle variant="h6">
                            <TrendingUpIcon />
                            Monthly Expense Trend
                        </CardTitle>

                        <Divider sx={{ mb: 2, opacity: 0.6 }} />

                        <ChartContainer>
                            <ResponsiveContainer width="99%" height="100%">
                                <BarChart
                                    data={stats.monthlyData}
                                    margin={{ top: 20, right: 20, left: 10, bottom: 30 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                    <XAxis
                                        dataKey="month"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#666', fontSize: 12 }}
                                        dy={10}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#666', fontSize: 12 }}
                                        tickFormatter={(value) => `${value / 1000}k`}
                                    />
                                    <RechartsTooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }} />
                                    <Bar
                                        dataKey="total"
                                        radius={[6, 6, 0, 0]}
                                        barSize={isMobile ? 20 : 40}
                                        animationDuration={1500}
                                    >
                                        {stats.monthlyData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartContainer>
                    </SummaryCard>
                </Grid>

                {/* Expense Breakdowns */}
                <Grid item xs={12} md={4}>
                    <GridContainer container spacing={3} sx={{ height: '100%' }}>

                        {/* Category Breakdown */}
                        <Grid item xs={12} sm={isMobile ? 6 : 12} sx={{ pb: isMobile ? 1.5 : 3 }}>
                            <SummaryCard>
                                <CardTitle variant="subtitle1">
                                    <CategoryIcon />
                                    Top Expense Categories
                                </CardTitle>

                                <Divider sx={{ mb: 2, opacity: 0.6 }} />

                                <Box sx={{
                                    overflowY: 'auto',
                                    flex: 1,
                                    maxHeight: isMobile ? '150px' : '200px',
                                    px: 0.5,
                                }}>
                                    {categoryData.length > 0 ? (
                                        categoryData.slice(0, 5).map((item, index) => (
                                            <ExpenseItem key={index} index={index}>
                                                <Typography variant="body2" noWrap sx={{ maxWidth: '60%', fontWeight: 600 }}>
                                                    {item.name}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
                                                    {item.value.toLocaleString('en-US', {
                                                        style: 'currency',
                                                        currency: 'KSH',
                                                        maximumFractionDigits: 0,
                                                    })}
                                                </Typography>
                                            </ExpenseItem>
                                        ))
                                    ) : (
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '100%',
                                            minHeight: 100,
                                            opacity: 0.7
                                        }}>
                                            <Typography variant="body2" color="text.secondary">
                                                No category data available
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                {categoryData.length > 5 && (
                                    <Box sx={{ mt: 1, textAlign: 'center', pt: 1, borderTop: '1px dashed #eee' }}>
                                        <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                                            And {categoryData.length - 5} more categories
                                        </Typography>
                                    </Box>
                                )}
                            </SummaryCard>
                        </Grid>

                        {/* Property Breakdown */}
                        <Grid item xs={12} sm={isMobile ? 6 : 12} sx={{ pb: isMobile ? 1.5 : 3, pl: 0 }}>
                            <SummaryCard>
                                <CardTitle variant="subtitle1">
                                    <HomeWorkIcon />
                                    Top Properties by Expense
                                </CardTitle>

                                <Divider sx={{ mb: 2, opacity: 0.6 }} />

                                <Box sx={{
                                    overflowY: 'auto',
                                    flex: 1,
                                    maxHeight: isMobile ? '150px' : '200px',
                                    px: 0, // Removed horizontal padding here
                                }}>
                                    {propertyData.length > 0 ? (
                                        propertyData.slice(0, 5).map((item, index) => (
                                            <ExpenseItem key={index} index={index}>
                                                <Typography variant="body2" noWrap sx={{ maxWidth: '60%', fontWeight: 600 }}>
                                                    {item.name}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
                                                    {item.value.toLocaleString('en-US', {
                                                        style: 'currency',
                                                        currency: 'KSH',
                                                        maximumFractionDigits: 0,
                                                    })}
                                                </Typography>
                                            </ExpenseItem>
                                        ))
                                    ) : (
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '100%',
                                            minHeight: 100,
                                            opacity: 0.7
                                        }}>
                                            <Typography variant="body2" color="text.secondary">
                                                No property data available
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                {propertyData.length > 5 && (
                                    <Box sx={{ mt: 1, textAlign: 'center', pt: 1, borderTop: '1px dashed #eee' }}>
                                        <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                                            And {propertyData.length - 5} more properties
                                        </Typography>
                                    </Box>
                                )}
                            </SummaryCard>
                        </Grid>
                    </GridContainer>
                </Grid>
            </GridContainer>
        </SummaryContainer>
    );
};

ExpenseSummary.propTypes = {
    stats: PropTypes.shape({
        totalExpenses: PropTypes.number.isRequired,
        totalAmount: PropTypes.number.isRequired,
        byCategory: PropTypes.object.isRequired,
        byProperty: PropTypes.object.isRequired,
        monthlyData: PropTypes.array.isRequired,
    }).isRequired,
    isMobile: PropTypes.bool.isRequired,
};

export default ExpenseSummary;